import { useAuth } from '@/contexts/auth-context';
import { isFullBleedPath } from '@/stores/nav-store';
import { useProductAnnouncementStore } from '@/stores/product-announcement-store';
import { Box, Button, Text, VisuallyHidden } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useProductAnnouncements } from './use-product-announcements';

/**
 * Top-bar trigger for the product update panel. Rendered inside `TopBar`,
 * whose surface is the dark `darkNav` chrome — the green wash and the white
 * alphas below are calibrated for that backdrop.
 *
 * It renders nothing when the brand has no undismissed update left, so the bar
 * never carries a control that opens an empty panel.
 */
export function FeatureAnnouncementIndicator() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const suppressed = isFullBleedPath(location.pathname);
  const brandId = suppressed ? null : (user?.currentBrandId ?? null);
  const { products, unseenCount } = useProductAnnouncements(brandId);
  const open = useProductAnnouncementStore((state) => state.open);
  const toggle = useProductAnnouncementStore((state) => state.toggle);

  if (!brandId || products.length === 0) return null;

  const hasUnseen = unseenCount > 0;

  return (
    <Button
      type="button"
      aria-expanded={open}
      onClick={toggle}
      variant="ghost"
      h="32px"
      minH="32px"
      px="12px"
      gap="8px"
      borderRadius="md"
      bg="announcement.chipBg"
      color={hasUnseen ? 'announcement.indicatorFg' : 'announcement.indicatorFgMuted'}
      _hover={{ bg: 'announcement.controlHoverBg' }}
      _active={{ bg: 'announcement.controlHoverBg' }}
      _focusVisible={{ outline: 'none', boxShadow: 'none' }}
    >
      {/* Same mark as the panel's own chip, so the trigger and what it opens
          read as one thing. The dot stays put once everything is read and only
          loses its halo — a mark that appears and disappears would shift the
          label sideways on a bar the eye keeps returning to. */}
      <Box
        aria-hidden="true"
        w="6px"
        h="6px"
        borderRadius="full"
        bg="announcement.accent"
        boxShadow={hasUnseen ? 'announcement.dot' : 'none'}
        opacity={hasUnseen ? 1 : 0.45}
        flexShrink={0}
      />
      <Text as="span" fontFamily="heading" fontSize="13px" fontWeight="medium" lineHeight="1">
        {t('announcements.product.indicatorLabel')}
      </Text>
      {hasUnseen ? (
        <VisuallyHidden>
          {t('announcements.product.unseenCount', { count: unseenCount })}
        </VisuallyHidden>
      ) : null}
    </Button>
  );
}
